import { CasClient } from '@central-products/cas-client';
import { subscribe, unsubscribe } from '@wh/message-bus';
import { abortable, SessionExistsError } from '../libs';
import { PassiveLoginStrategyInterface } from '../types';

export class PassiveLoginNativeStrategy implements PassiveLoginStrategyInterface {
  private abortController?: AbortController;
  private nativeLoginSubscriptionToken?: string;

  constructor(
    private config: {
      casClient: CasClient;
      onLoginCallback: () => void;
    }
  ) {}

  public async init() {
    try {
      this.abortController = new AbortController();

      await Promise.race([this.watchAbort(), this.watchLogin()]);
    } catch (error) {
      this.close();

      if (error instanceof SessionExistsError) {
        this.config.onLoginCallback();
      }
    }
  }

  public close() {
    if (!this.abortController) {
      return;
    }

    if (!this.abortController.signal.aborted) {
      this.abortController.abort();
    }

    if (!this.nativeLoginSubscriptionToken) {
      return;
    }

    unsubscribe(this.nativeLoginSubscriptionToken);
  }

  private watchAbort() {
    return new Promise((_, reject) => {
      const onAbortCallback = () => {
        this.abortController!.signal.removeEventListener('abort', onAbortCallback);
        reject();
      };

      this.abortController!.signal.addEventListener('abort', onAbortCallback);
    });
  }

  private watchLogin() {
    return new Promise((_, reject) => {
      /**
       * Sportsbook native apps for UK emit `native.login` event with
       * payload of `1` (logged in) or `0` (not logged in).
       *
       * We attach to this event and when we receive `1` as payload
       * we then call CAS to perform session check.
       */
      this.nativeLoginSubscriptionToken = subscribe({
        topic: 'native.login',
        callback: async (envelope) => {
          if (Number.parseInt(envelope.data, 10) !== 1) {
            return;
          }

          try {
            /**
             * Attempt to check if CAS session exists.
             *
             * If this call resolves this means valid CAS session is found or one is created
             * based on `cust_*` cookies (when `custLogin` flag is set to `true`) and we
             * should reject with `SessionExistsError` and inform CAS session manager
             * to change it's state into `LOGGED_IN`.
             */
            await abortable(() => this.config.casClient.init({ custLogin: true }), {
              signal: this.abortController!.signal,
            });
            reject(new SessionExistsError());
          } catch (error) {
            reject(error);
          }
        },
      });
    });
  }
}
